/**
 * IndexedDB cache of motion-timeline media (music now, video clips in Phase 5).
 *
 * This is the PRIMARY read path, not just a cache: blobs read from here
 * become blob: URLs, which never hit Storage CORS and never taint a canvas.
 * Storage is the backup and the cross-device source. Shape mirrors
 * lib/pdf/pdfSourceIdb.ts.
 */

const DB_NAME = 'craftara-video-assets';
const STORE = 'assets';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });
}

export async function putVideoAsset(key: string, blob: Blob): Promise<void> {
  if (!key || !blob || blob.size === 0) return;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB put failed'));
    });
  } finally {
    db.close();
  }
}

export async function getVideoAsset(key: string): Promise<Blob | null> {
  if (!key) return null;
  const db = await openDb();
  try {
    const blob = await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const val = req.result;
        resolve(val instanceof Blob ? val : null);
      };
      req.onerror = () => reject(req.error || new Error('IndexedDB get failed'));
    });
    return blob && blob.size > 0 ? blob : null;
  } finally {
    db.close();
  }
}

export async function deleteVideoAsset(key: string): Promise<void> {
  if (!key) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB delete failed'));
    });
    db.close();
  } catch {
    /* ignore */
  }
}

export async function listVideoAssets(): Promise<string[]> {
  const db = await openDb();
  try {
    return await new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAllKeys();
      req.onsuccess = () => resolve(req.result.map(String));
      req.onerror = () => reject(req.error || new Error('IndexedDB list failed'));
    });
  } finally {
    db.close();
  }
}
